import { useQuery } from '@tanstack/react-query'
import { type OrgTypeDTO, organizationService } from '@/services/organization.service'
import {
	listState,
	type OrgT,
	useConfirmState,
	useDialogMutations,
	useEditorState,
	viewConfirm,
	viewEditor,
} from './org-editor'
import {
	buildTypeCreateBody,
	buildTypePatch,
	EMPTY_TYPE_FORM,
	type TypeFormValues,
	typeFormErrors,
	typeToForm,
} from './type-form'

export const ORG_TYPES_KEY = ['org-types'] as const

/** Every unit type in the API's order (sortOrder, then code). */
export const useOrgTypeList = (enabled: boolean) =>
	useQuery({ queryKey: ORG_TYPES_KEY, queryFn: () => organizationService.types(), enabled })

/** The Types tab (REQ-009): list, create/edit dialog with hierarchy rules, guarded delete. */
export function useOrgTypes({ t }: { t: OrgT }) {
	const list = useOrgTypeList(true)
	const form = useEditorState<TypeFormValues, { original: OrgTypeDTO | null }>(t)
	const deleting = useConfirmState(t)
	const m = useDialogMutations({
		queryKey: ORG_TYPES_KEY,
		form,
		deleting,
		save: ({ values, extra: { original } }) =>
			original
				? organizationService.updateType(original.id, buildTypePatch(original, values))
				: organizationService.createType(buildTypeCreateBody(values)),
		remove: (id) => organizationService.deleteType(id),
	})
	const types = list.data ?? []
	const byId = (id: string) => types.find((type) => type.id === id)
	const editedCode = form.editor?.extra.original?.code
	return {
		state: listState(list),
		rows: types,
		parentTypeOptions: types.map((type) => type.code).filter((code) => code !== editedCode),
		retry: () => void list.refetch(),
		editor: viewEditor(form.editor),
		deleting: viewConfirm(deleting.pending),
		isBusy: m.isBusy,
		openNew: () => form.open('create', EMPTY_TYPE_FORM, { original: null }),
		openEdit: (id: string) => {
			const original = byId(id)
			if (original) form.open('edit', typeToForm(original), { original })
		},
		change: form.change,
		close: form.close,
		submit: () => m.submit(typeFormErrors),
		askDelete: (id: string) => deleting.ask(id, byId(id)?.name ?? ''),
		cancelDelete: deleting.cancel,
		confirmDelete: m.confirmDelete,
	}
}
